import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
  type RefObject,
  type KeyboardEvent,
  type ClipboardEvent,
} from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import {
  Send,
  ChevronDown,
  Check,
  Square,
  Menu,
  RotateCcw,
  Search,
  Trash2,
  Paperclip,
  X,
  FileText,
  Plus,
  AtSign,
  ArrowDown,
  Pin,
  Loader2,
} from 'lucide-react';

import {
  useShakespeare,
  useShakespeareCreditsBalance,
  type Model,
} from '@/hooks/useShakespeare';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollows } from '@/hooks/useFollows';
import { useAppContext } from '@/hooks/useAppContext';
import { useNetworkListsSync } from '@/hooks/useNetworkListsSync';
import { LoginArea } from '@/components/auth/LoginArea';
import { DorkThinking } from '@/components/DorkThinking';
import { cn } from '@/lib/utils';
import {
  useWorkspace,
  type WorkspaceSearchHit,
} from '@/hooks/useWorkspace';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import {
  buildApiMessagesDetailed,
  buildContextPreview,
  clipForPin,
  resolveGroundedOnly,
  resolvePersonaId,
} from '@/lib/workspace/prompt';
import type { PersonaId } from '@/lib/workspace/personas';
import type {
  ChatMention,
  Message,
  MessageAttachment,
  MessageReceipt,
  NostrContextChip,
} from '@/lib/workspace/types';
import {
  isContextCacheStale,
  isLiveContextPack,
} from '@/lib/workspace/types';
import {
  attachmentsFromClipboardEvent,
  attachmentsFromFileList,
  mergeAttachments,
} from '@/lib/workspace/attachments';
import {
  contextMeterLevel,
  estimateOutgoingTokens,
  formatTokenCount,
  resolveContextWindow,
} from '@/lib/workspace/tokens';
import {
  buildNostrFetchBlock,
  extractNostrRefs,
  fetchNostrEntity,
  normalizeRefKey,
  parseNostrRef,
} from '@/lib/nostr/fetchEntity';
import {
  buildMentionsBlock,
  buildPacksBlock,
  chipFromMention,
  expandChipSafely,
  makeEventChip,
  makeNetworkSearchChip,
  makeProfileChip,
  makeSelfChip,
  mergeChipsAndMentions,
  messageWantsNetworkSearch,
  npubOf,
} from '@/lib/nostr/packs';
import {
  applyMentionToText,
  filterMentionCandidates,
  getMentionQueryAtCaret,
  isNetworkSearchCandidate,
  looksLikeNip05,
  NOSTR_NETWORK_SENTINEL,
  resolveNip05,
  syncMentionsWithText,
  verifyNip05ForPubkey,
  type MentionCandidate,
} from '@/lib/nostr/mentions';
import type { SessionBlockInput } from '@/lib/nostr/sessionBlock';
import { getProfileCached } from '@/lib/profileCache';
import { OpenDorkWordmark } from '@/components/workspace/OpenDorkWordmark';
import { WorkspaceSidebar } from '@/components/workspace/WorkspaceSidebar';
import {
  SettingsModal,
  type SettingsTab,
} from '@/components/workspace/SettingsModal';
import { VaultUnlockGate } from '@/components/workspace/VaultUnlockGate';
import { AccountIsolationGate } from '@/components/workspace/AccountIsolationGate';
import {
  MessageLine,
  type MessageActions,
  type RewritePreset,
} from '@/components/workspace/MessageLine';
import { ProjectHubView } from '@/components/workspace/ProjectHubView';
import { ChatSearchDialog } from '@/components/workspace/ChatSearchDialog';
import { NostrPicker } from '@/components/workspace/NostrPicker';
import { NostrContextChips } from '@/components/workspace/NostrContextChips';
import { MentionTypeahead } from '@/components/workspace/MentionTypeahead';
import { ContextPreviewStrip } from '@/components/workspace/ContextPreviewStrip';
import { StylePicker } from '@/components/workspace/StylePicker';
import { restoreBodyInteractivity } from '@/components/ui/dialog';

// ─── Model picker (lives near the composer) ───

interface ModelPickerProps {
  models: Model[];
  modelsLoading: boolean;
  selectedModel: string;
  onSelect: (fullId: string) => void;
  creditsBalance: number | null;
  usingShakespeare: boolean;
  onOpenProviders: () => void;
  /** Dropdown opens upward (composer) or downward */
  placement?: 'up' | 'down';
}

function ModelPicker({
  models,
  modelsLoading,
  selectedModel,
  onSelect,
  creditsBalance,
  usingShakespeare,
  onOpenProviders,
  placement = 'up',
}: ModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;
    const menuWidth = 320;
    const pad = 12;
    let left = rect.left;
    // Keep menu on-screen horizontally
    left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));

    if (placement === 'up') {
      const spaceAbove = rect.top - pad;
      const maxHeight = Math.min(320, Math.max(160, spaceAbove - gap));
      setMenuPos({
        bottom: window.innerHeight - rect.top + gap,
        left,
        maxHeight,
      });
    } else {
      const spaceBelow = window.innerHeight - rect.bottom - pad;
      const maxHeight = Math.min(320, Math.max(160, spaceBelow - gap));
      setMenuPos({
        top: rect.bottom + gap,
        left,
        maxHeight,
      });
    }
  }, [placement]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    // Capture scroll from any ancestor
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const filtered = query.trim()
    ? models.filter((m) => {
        const q = query.toLowerCase();
        return (
          m.name.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          m.owned_by?.toLowerCase().includes(q) ||
          m.provider?.toLowerCase().includes(q)
        );
      })
    : models;

  const grouped = filtered.reduce<Record<string, Model[]>>((acc, model) => {
    const key = model.provider || 'shakespeare';
    if (!acc[key]) acc[key] = [];
    acc[key].push(model);
    return acc;
  }, {});

  const providerNames: Record<string, string> = {};
  models.forEach((m) => {
    if (!providerNames[m.provider]) {
      providerNames[m.provider] = m.owned_by || m.provider;
    }
  });

  const selectedName =
    models.find((m) => m.fullId === selectedModel)?.name ||
    (modelsLoading ? 'Loading…' : models.length === 0 ? 'Select model' : selectedModel || 'Select model');

  const menu = open && menuPos && (
    <div
      ref={menuRef}
      role="listbox"
      className="fixed w-80 bg-[#161616] border border-[#2a2a2a] rounded-xl shadow-2xl z-[100] flex flex-col overflow-hidden od-enter"
      style={{
        left: menuPos.left,
        top: menuPos.top,
        bottom: menuPos.bottom,
        height: menuPos.maxHeight,
        maxHeight: menuPos.maxHeight,
      }}
    >
      <div className="px-2 pb-1.5 pt-1.5 border-b border-[#242424] shrink-0">
        <div className="flex items-center gap-1.5 px-2.5 py-2 bg-[#0f0f0f] rounded-lg border border-[#2a2a2a]">
          <Search className="w-3.5 h-3.5 text-[#3a3a3a] shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search models…"
            className="w-full bg-transparent text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none"
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 py-1 scrollbar-thin">
        {Object.keys(grouped).length === 0 && (
          <div className="px-3 py-6 text-center space-y-2">
            <p className="text-[12px] text-[#5a5a5a]">
              {models.length === 0 ? 'No models available' : 'No models match'}
            </p>
            {models.length === 0 && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenProviders();
                }}
                className="text-[12px] text-[#e8e6e3] underline underline-offset-2"
              >
                Add a provider
              </button>
            )}
          </div>
        )}
        {Object.entries(grouped).map(([providerId, providerModels]) => (
          <div key={providerId}>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3a3a3a] font-semibold sticky top-0 bg-[#1a1a1a]">
              {providerNames[providerId] || providerId}
            </div>
            {providerModels.map((model) => (
              <button
                key={model.fullId}
                type="button"
                role="option"
                aria-selected={selectedModel === model.fullId}
                onClick={() => {
                  onSelect(model.fullId);
                  setOpen(false);
                  setQuery('');
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-[13px] hover:bg-[#242424] transition-colors',
                  selectedModel === model.fullId
                    ? 'text-[#e8e6e3]'
                    : 'text-[#8a8884]',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{model.name}</span>
                  {selectedModel === model.fullId && (
                    <Check className="w-3.5 h-3.5 text-[#e8e6e3] shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="border-t border-[#242424] px-2 py-1.5 shrink-0">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onOpenProviders();
          }}
          className="w-full text-left px-3 py-2 text-[12px] text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#242424] rounded-lg transition-colors"
        >
          Manage providers…
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative flex items-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (models.length === 0 && !modelsLoading) {
            onOpenProviders();
            return;
          }
          if (!open) {
            // Position before paint so menu doesn't flash at 0,0
            const el = triggerRef.current;
            if (el) {
              const rect = el.getBoundingClientRect();
              const gap = 8;
              const menuWidth = 320;
              const pad = 12;
              let left = rect.left;
              left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));
              if (placement === 'up') {
                const spaceAbove = rect.top - pad;
                setMenuPos({
                  bottom: window.innerHeight - rect.top + gap,
                  left,
                  maxHeight: Math.min(320, Math.max(160, spaceAbove - gap)),
                });
              } else {
                const spaceBelow = window.innerHeight - rect.bottom - pad;
                setMenuPos({
                  top: rect.bottom + gap,
                  left,
                  maxHeight: Math.min(320, Math.max(160, spaceBelow - gap)),
                });
              }
            }
          }
          setOpen(!open);
          setQuery('');
        }}
        disabled={modelsLoading}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] transition-colors disabled:opacity-50 max-w-[240px]',
          open
            ? 'text-[#e8e6e3] bg-[#1a1a1a]'
            : 'text-[#8a8884] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]',
        )}
      >
        <span className="truncate font-medium">{selectedName}</span>
        <ChevronDown
          className={cn('w-3 h-3 shrink-0 opacity-60 transition-transform', open && 'rotate-180')}
        />
      </button>

      {usingShakespeare && creditsBalance !== null && (
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] text-[#5a5a5a] hover:text-[#8a8884] transition-colors"
          title="Shakespeare credits"
        >
          ${creditsBalance.toFixed(2)}
        </a>
      )}

      {menu}
    </div>
  );
}

// ─── Composer ───

interface ChatInputProps {
  centered?: boolean;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  input: string;
  setInput: (val: string) => void;
  handleKeyDown: (e: KeyboardEvent) => void;
  handleSend: () => void;
  handleStop?: () => void;
  selectedModel: string;
  isStreaming: boolean;
  modelPicker: ReactNode;
  /** Privacy: what leaves the browser with this request */
  contextHint?: string | null;
  usingShakespeare?: boolean;
  attachments: MessageAttachment[];
  onAttachmentsChange: (next: MessageAttachment[]) => void;
  onAttachError?: (msg: string) => void;
  /** Token meter */
  tokenUsed?: number;
  tokenWindow?: number;
  /** Layers that will be injected (composer preview) */
  contextLayers?: import('@/lib/workspace/prompt').ContextPreviewLayer[];
  /** Toggle a context layer or nested item for this chat */
  onContextToggle?: (key: string, enabled: boolean) => void;
  /** Active chat's thread instructions (strip editor) */
  threadInstructions?: string;
  onSaveThreadInstructions?: (text: string) => void;
  /** Response style + grounded picker (toolbar) */
  stylePicker?: ReactNode;
  /** Nostr context chips */
  nostrChips?: NostrContextChip[];
  onNostrChipsChange?: (next: NostrContextChip[]) => void;
  /** @mentions */
  mentions?: ChatMention[];
  onMentionsChange?: (next: ChatMention[]) => void;
  mentionCandidates?: MentionCandidate[];
  mentionsLoading?: boolean;
  onOpenNostrPicker?: () => void;
  /** Resolve a pasted nostr ref into a composer chip */
  onAddRefChip?: (ref: string) => Promise<void> | void;
  /** Pin a pasted nostr ref to the active project context (when in one) */
  onPinRefToProject?: (ref: string) => Promise<void> | void;
}

function ChatInput({
  centered = false,
  inputRef,
  input,
  setInput,
  handleKeyDown,
  handleSend,
  handleStop,
  selectedModel,
  isStreaming,
  modelPicker,
  contextHint,
  usingShakespeare,
  attachments,
  onAttachmentsChange,
  onAttachError,
  tokenUsed,
  tokenWindow,
  contextLayers,
  onContextToggle,
  threadInstructions,
  onSaveThreadInstructions,
  stylePicker,
  nostrChips = [],
  onNostrChipsChange,
  mentions = [],
  onMentionsChange,
  mentionCandidates = [],
  mentionsLoading,
  onOpenNostrPicker,
  onAddRefChip,
  onPinRefToProject,
}: ChatInputProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentionAnchor, setMentionAnchor] = useState<{
    top: number;
    left: number;
    bottom: number;
  } | null>(null);
  const [nip05Extra, setNip05Extra] = useState<MentionCandidate[]>([]);
  const [refBarBusy, setRefBarBusy] = useState(false);
  const [dismissedRefKey, setDismissedRefKey] = useState<string | null>(null);
  // Latest arrays for async mention/chip updates (NIP-05 verify callback)
  const mentionsRef = useRef(mentions);
  const chipsRef = useRef(nostrChips);
  useEffect(() => {
    mentionsRef.current = mentions;
  }, [mentions]);
  useEffect(() => {
    chipsRef.current = nostrChips;
  }, [nostrChips]);

  // Pasted nostr ref that isn't covered by a chip/mention yet — offer to
  // turn it into a rich chip (or pin it to the project) instead of leaving
  // it as bare text that only resolves at send time.
  const detectedRef = useMemo(() => {
    if (!onAddRefChip) return null;
    const refs = extractNostrRefs(input || '');
    if (!refs.length) return null;
    const chipKeys = new Set(nostrChips.map((c) => normalizeRefKey(c.ref)));
    const mentionKeys = new Set(mentions.map((m) => normalizeRefKey(m.npub)));
    for (const r of refs) {
      const key = normalizeRefKey(r);
      if (chipKeys.has(key) || mentionKeys.has(key)) continue;
      return r;
    }
    return null;
  }, [input, nostrChips, mentions, onAddRefChip]);

  useEffect(() => {
    if (!input.trim()) setDismissedRefKey(null);
  }, [input]);

  const showRefBar =
    !!detectedRef &&
    normalizeRefKey(detectedRef) !== dismissedRefKey &&
    !isStreaming &&
    !mentionOpen;

  const handleRefBarAction = async (
    action: ((ref: string) => Promise<void> | void) | undefined,
  ) => {
    if (!action || !detectedRef) return;
    const ref = detectedRef;
    setRefBarBusy(true);
    try {
      await action(ref);
      setDismissedRefKey(normalizeRefKey(ref));
    } finally {
      setRefBarBusy(false);
    }
  };

  const canSend =
    !!selectedModel &&
    !isStreaming &&
    (!!input?.trim() ||
      attachments.length > 0 ||
      nostrChips.length > 0 ||
      mentions.length > 0);

  const filteredMentions = useMemo(() => {
    const base = filterMentionCandidates(mentionCandidates, mentionQuery, 8);
    const extras = nip05Extra.filter(
      (e) => !base.some((b) => b.pubkey === e.pubkey),
    );
    return [...extras, ...base].slice(0, 10);
  }, [mentionCandidates, mentionQuery, nip05Extra]);

  // NIP-05 resolve while typing @name@domain
  useEffect(() => {
    if (!mentionOpen || !looksLikeNip05(mentionQuery)) {
      setNip05Extra([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      void resolveNip05(mentionQuery, {
        corsProxyTemplate: 'https://proxy.shakespeare.diy/?url={href}',
      }).then((hit) => {
        if (cancelled || !hit) {
          if (!cancelled) setNip05Extra([]);
          return;
        }
        setNip05Extra([
          {
            pubkey: hit.pubkey,
            npub: npubOf(hit.pubkey),
            displayName: mentionQuery.replace(/^@/, '').split('@')[0] || 'user',
            nip05: mentionQuery.replace(/^@/, ''),
            source: 'nip05',
          },
        ]);
      });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mentionQuery, mentionOpen]);

  useEffect(() => {
    if (!plusOpen) return;
    const onDown = (e: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target as Node)) {
        setPlusOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [plusOpen]);

  const updateMentionState = (text: string, caret: number) => {
    const q = getMentionQueryAtCaret(text, caret);
    if (!q) {
      setMentionOpen(false);
      setMentionQuery('');
      return;
    }
    setMentionQuery(q.query);
    setMentionOpen(true);
    setMentionIdx(0);
    const el = inputRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setMentionAnchor({
        top: rect.top,
        left: rect.left + 12,
        bottom: rect.bottom,
      });
    }
  };

  const selectMention = (c: MentionCandidate) => {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? input.length;

    // Special @nostr network search — chip only, not a person mention
    if (isNetworkSearchCandidate(c)) {
      const { text, caret: nextCaret } = applyMentionToText(input, caret, {
        ...c,
        displayName: 'nostr',
      });
      // Drop the ChatMention side-effect: only keep text + network chip
      setInput(text);
      if (onNostrChipsChange) {
        const chip = makeNetworkSearchChip();
        onNostrChipsChange([
          ...nostrChips.filter((x) => x.pack !== 'network-search'),
          chip,
        ]);
      }
      setMentionOpen(false);
      setMentionQuery('');
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(nextCaret, nextCaret);
      });
      return;
    }

    // Prefer picture from candidate, then profile cache
    let picture = c.picture;
    if (!picture) {
      const cached = getProfileCached(c.pubkey);
      const m = cached?.metadata;
      if (typeof m?.picture === 'string') picture = m.picture;
    }
    const enriched = { ...c, picture };

    const { text, caret: nextCaret, mention } = applyMentionToText(
      input,
      caret,
      enriched,
    );
    // Ensure mention carries picture for chip UI
    if (picture && !mention.picture) mention.picture = picture;

    setInput(text);
    const nextMentions = [
      ...mentions.filter((m) => m.pubkey !== mention.pubkey),
      mention,
    ];
    onMentionsChange?.(nextMentions);

    // Mirror + picker: also add a visual context chip with avatar
    if (onNostrChipsChange) {
      const chip = chipFromMention(mention);
      onNostrChipsChange([
        ...nostrChips.filter(
          (x) => x.pubkey?.toLowerCase() !== mention.pubkey.toLowerCase(),
        ),
        chip,
      ]);
    }

    // Background-verify the declared NIP-05 so mention/chip can show a
    // verified tick (cached per session; direct fetch, proxy fallback).
    if (mention.nip05) {
      const pk = mention.pubkey;
      const addr = mention.nip05;
      void verifyNip05ForPubkey(addr, pk).then((ok) => {
        onMentionsChange?.(
          (mentionsRef.current ?? []).map((m) =>
            m.pubkey === pk && m.nip05 === addr ? { ...m, nip05Verified: ok } : m,
          ),
        );
        onNostrChipsChange?.(
          (chipsRef.current ?? []).map((x) =>
            x.pubkey?.toLowerCase() === pk.toLowerCase() && x.nip05 === addr
              ? { ...x, nip05Verified: ok }
              : x,
          ),
        );
      });
    }

    setMentionOpen(false);
    setMentionQuery('');
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const addFiles = async (files: FileList | File[]) => {
    const results = await attachmentsFromFileList(files);
    const ok: MessageAttachment[] = [];
    for (const r of results) {
      if (r.ok) ok.push(r.attachment);
      else onAttachError?.(r.error);
    }
    if (ok.length) {
      const { next, error } = mergeAttachments(attachments, ok);
      onAttachmentsChange(next);
      if (error) onAttachError?.(error);
    }
  };

  const onPaste = async (e: ClipboardEvent) => {
    const results = await attachmentsFromClipboardEvent(e.nativeEvent);
    if (!results.length) return;
    const ok: MessageAttachment[] = [];
    let any = false;
    for (const r of results) {
      any = true;
      if (r.ok) ok.push(r.attachment);
      else onAttachError?.(r.error);
    }
    if (any && ok.length) {
      e.preventDefault();
      const { next, error } = mergeAttachments(attachments, ok);
      onAttachmentsChange(next);
      if (error) onAttachError?.(error);
    }
  };

  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const c = filteredMentions[mentionIdx];
        if (c) selectMention(c);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    handleKeyDown(e);
  };

  const meterLevel =
    tokenUsed != null && tokenWindow
      ? contextMeterLevel(tokenUsed, tokenWindow)
      : 'ok';
  const meterPct =
    tokenUsed != null && tokenWindow && tokenWindow > 0
      ? Math.min(100, Math.round((tokenUsed / tokenWindow) * 100))
      : 0;

  return (
    <div className={cn('w-full max-w-3xl mx-auto', centered && 'px-4')}>
      {contextLayers && contextLayers.length > 0 && (
        <div className="mb-2">
          <ContextPreviewStrip
            layers={contextLayers}
            onToggle={onContextToggle}
            disabled={isStreaming}
            threadInstructions={threadInstructions}
            onSaveThreadInstructions={onSaveThreadInstructions}
          />
        </div>
      )}
      {showRefBar && detectedRef && (
        <div className="mb-2 flex items-center gap-2 rounded-xl border border-[#2a2a2a] bg-[#101010] px-3 py-2 od-enter">
          <AtSign className="w-3.5 h-3.5 text-[#5a5a5a] shrink-0" />
          <span
            className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#8a8884]"
            title={detectedRef}
          >
            {detectedRef.length > 46
              ? `${detectedRef.slice(0, 24)}…${detectedRef.slice(-16)}`
              : detectedRef}
          </span>
          {refBarBusy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5a5a5a] shrink-0" />
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleRefBarAction(onAddRefChip)}
                className="shrink-0 px-2 py-1 rounded-md bg-[#e8e6e3] text-[#0a0a0a] text-[10px] font-medium"
                title="Resolve now and attach as a context chip"
              >
                Add chip
              </button>
              {onPinRefToProject && (
                <button
                  type="button"
                  onClick={() => void handleRefBarAction(onPinRefToProject)}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#2a2a2a] text-[10px] text-[#8a8884] hover:text-[#e8e6e3]"
                  title="Pin to this project’s context pack"
                >
                  <Pin className="w-2.5 h-2.5" />
                  Project
                </button>
              )}
              <button
                type="button"
                onClick={() => setDismissedRefKey(normalizeRefKey(detectedRef))}
                className="shrink-0 p-1 rounded text-[#3a3a3a] hover:text-[#8a8884]"
                title="Dismiss (ref still resolves at send)"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      )}
      <div
        className={cn(
          'od-composer',
          dragOver && 'border-[#5a5a5a] bg-[#161616] ring-1 ring-[#3a3a3a]/40',
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) {
            void addFiles(e.dataTransfer.files);
          }
        }}
      >
        {(attachments.length > 0 || nostrChips.length > 0) && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {nostrChips.length > 0 && onNostrChipsChange && (
              <NostrContextChips
                chips={nostrChips}
                onRemove={(id) => {
                  const removed = nostrChips.find((c) => c.id === id);
                  onNostrChipsChange(nostrChips.filter((c) => c.id !== id));
                  // Keep @text + mentions list in sync when dismissing a mention chip
                  if (removed?.fromMention && removed.pubkey && onMentionsChange) {
                    onMentionsChange(
                      mentions.filter(
                        (m) => m.pubkey.toLowerCase() !== removed.pubkey!.toLowerCase(),
                      ),
                    );
                  }
                }}
              />
            )}
            {attachments.map((a) => (
              <div
                key={a.id}
                className="relative group/att rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] overflow-hidden"
              >
                {a.kind === 'image' ? (
                  <img
                    src={a.data}
                    alt={a.name || 'image'}
                    className="h-16 w-16 object-cover"
                  />
                ) : (
                  <div className="h-16 min-w-[4rem] max-w-[140px] px-2 flex items-center gap-1.5 text-[11px] text-[#8a8884]">
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{a.name || 'file'}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() =>
                    onAttachmentsChange(attachments.filter((x) => x.id !== a.id))
                  }
                  className="absolute top-0.5 right-0.5 p-0.5 rounded-md bg-black/70 text-[#e8e6e3] opacity-0 group-hover/att:opacity-100 transition-opacity"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => {
            const v = e.target.value;
            setInput(v);
            const caret = e.target.selectionStart ?? v.length;
            updateMentionState(v, caret);
            if (onMentionsChange && mentions.length) {
              const kept = syncMentionsWithText(v, mentions);
              onMentionsChange(kept);
              // Drop mention-origin chips for removed @tags
              if (onNostrChipsChange && nostrChips.some((c) => c.fromMention)) {
                const keptPks = new Set(kept.map((m) => m.pubkey.toLowerCase()));
                onNostrChipsChange(
                  nostrChips.filter(
                    (c) =>
                      !c.fromMention ||
                      (c.pubkey && keptPks.has(c.pubkey.toLowerCase())),
                  ),
                );
              }
            }
          }}
          onKeyDown={onComposerKeyDown}
          onClick={(e) => {
            const t = e.currentTarget;
            updateMentionState(t.value, t.selectionStart ?? 0);
          }}
          onPaste={(e) => void onPaste(e)}
          placeholder={
            !selectedModel
              ? 'Select a model to get started…'
              : 'How can I help?  @people  ·  + for Nostr & files'
          }
          disabled={!selectedModel || isStreaming}
          rows={1}
          className="w-full bg-transparent border-0 px-4 pt-3.5 pb-2 text-[15px] text-[#e8e6e3] placeholder:text-[#4a4a4a] resize-none focus:outline-none focus:ring-0 disabled:opacity-50 leading-relaxed rounded-t-[1.25rem]"
          style={{ minHeight: '56px', maxHeight: '220px' }}
        />
        <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-0.5 relative z-10">
          <div className="min-w-0 flex items-center gap-0.5">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.txt,.md,.json,.csv,.ts,.tsx,.js,.jsx,.py,.go,.rs,.yml,.yaml,.toml,.html,.css,.svg,.log"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div className="relative" ref={plusMenuRef}>
              <button
                type="button"
                onClick={() => setPlusOpen((o) => !o)}
                disabled={!selectedModel || isStreaming}
                className={cn(
                  'p-1.5 rounded-lg transition-colors disabled:opacity-40',
                  plusOpen
                    ? 'text-[#e8e6e3] bg-[#1a1a1a]'
                    : 'text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]',
                )}
                title="Add context"
                aria-expanded={plusOpen}
              >
                <Plus className="w-4 h-4" />
              </button>
              {plusOpen && (
                <div className="absolute bottom-full left-0 mb-1.5 w-56 rounded-xl border border-[#2a2a2a] bg-[#141414] shadow-2xl z-30 py-1 overflow-hidden od-enter">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[#e8e6e3] hover:bg-[#1a1a1a] text-left"
                    onClick={() => {
                      setPlusOpen(false);
                      onOpenNostrPicker?.();
                    }}
                  >
                    <AtSign className="w-3.5 h-3.5 text-[#5a5a5a]" />
                    <span className="flex-1">Nostr context</span>
                    <span className="text-[10px] text-[#3a3a3a]">packs</span>
                  </button>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[#e8e6e3] hover:bg-[#1a1a1a] text-left"
                    onClick={() => {
                      setPlusOpen(false);
                      fileRef.current?.click();
                    }}
                  >
                    <Paperclip className="w-3.5 h-3.5 text-[#5a5a5a]" />
                    <span className="flex-1">File or image</span>
                    <span className="text-[10px] text-[#3a3a3a]">paste</span>
                  </button>
                </div>
              )}
            </div>
            <div className="min-w-0 flex items-center">{modelPicker}</div>
            {stylePicker && (
              <div className="min-w-0 flex items-center">{stylePicker}</div>
            )}
          </div>
          {isStreaming ? (
            <button
              type="button"
              onClick={handleStop}
              className="shrink-0 p-2 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] text-red-400/80 hover:text-red-400 hover:border-red-900/40 transition-colors"
              title="Stop generation (Esc)"
            >
              <Square className="w-4 h-4 fill-current" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="od-send shrink-0 p-2"
              title="Send (Enter)"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <MentionTypeahead
        open={mentionOpen}
        candidates={filteredMentions}
        activeIndex={mentionIdx}
        onActiveIndexChange={setMentionIdx}
        onSelect={selectMention}
        onClose={() => setMentionOpen(false)}
        loading={mentionsLoading}
        query={mentionQuery}
        anchorRect={mentionAnchor}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-1 text-[10px] text-[#3a3a3a] leading-relaxed">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          {tokenUsed != null && tokenWindow != null && (
            <span
              className={cn(
                'tabular-nums',
                meterLevel === 'warn' && 'text-amber-500/80',
                meterLevel === 'critical' && 'text-red-400/90',
              )}
              title="Estimated tokens for next request (approx)"
            >
              ~{formatTokenCount(tokenUsed)} / {formatTokenCount(tokenWindow)}
              {meterPct >= 75 ? ` · ${meterPct}%` : ''}
            </span>
          )}
          {usingShakespeare && (
            <span className="hidden sm:inline truncate">Signed as your npub</span>
          )}
          {contextHint && !contextLayers?.length && <span className="truncate">{contextHint}</span>}
        </div>
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 hover:text-[#5a5a5a] transition-colors"
        >
          Vibed with Shakespeare
        </a>
      </div>
    </div>
  );
}

// ─── Page ───

/** First-name-ish label from Nostr kind-0 metadata for empty-state greetings. */
function greetingNameFromMetadata(
  metadata?: { name?: string; display_name?: string } | null,
): string | null {
  const raw = (metadata?.display_name || metadata?.name || '').trim();
  if (!raw) return null;
  // Prefer a short first token so “Jared Logan” → “Jared”, not a long wall of text.
  const first = raw.split(/\s+/)[0];
  return first || null;
}

export function AIChatPage() {
  const { user, metadata, isLoading: profileLoading } = useCurrentUser();
  const { nostr } = useNostr();
  const {
    candidates: mentionCandidates,
    followPubkeys,
    isLoading: followsLoading,
  } = useFollows();
  const { config: appConfig } = useAppContext();
  // Keep NIP-65 / Blossom auto-pull alive for the session (not only Settings)
  useNetworkListsSync();
  const {
    ready,
    messages,
    activeProject,
    activeChat,
    settings,
    projects,
    addMessage,
    updateMessage,
    clearChatMessages,
    replaceMessages,
    setDefaultModelId,
    createChat,
    selectChat,
    addContextItem,
    addMemoryItem,
    selectProject,
    updateProject,
    updateChat,
    updateSettings,
    deleteProject,
    exportProject,
    forkChat,
  } = useWorkspace();
  const {
    sendStreamingMessage,
    getAvailableModels,
    error: apiError,
    retryAfter,
    clearError,
  } = useShakespeare();
  const creditsBalance = useShakespeareCreditsBalance();

  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [nostrChips, setNostrChips] = useState<NostrContextChip[]>([]);
  const [mentions, setMentions] = useState<ChatMention[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('instructions');
  const [modelsVersion, setModelsVersion] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState<{ id: string; content: string } | null>(null);
  /** Claude-style project hub (instructions / memory / context / chats). */
  const [hubProjectId, setHubProjectId] = useState<string | null>(null);
  const [hubInput, setHubInput] = useState('');
  const [hubAttachments, setHubAttachments] = useState<MessageAttachment[]>([]);
  const [hubNostrChips, setHubNostrChips] = useState<NostrContextChip[]>([]);
  const [hubMentions, setHubMentions] = useState<ChatMention[]>([]);
  /** Layer omits for the hub composer (applied to the chat created on first send). */
  const [hubPromptOmit, setHubPromptOmit] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null);
  const [nostrPickerOpen, setNostrPickerOpen] = useState(false);
  const [nostrPickerMode, setNostrPickerMode] = useState<'composer' | 'project'>('composer');
  /** Which composer receives picker chips: main | hub | project-context */
  const [nostrPickerTarget, setNostrPickerTarget] = useState<'main' | 'hub' | 'project'>('main');
  const hubInputRef = useRef<HTMLTextAreaElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingIdRef = useRef<string | null>(null);
  /** Guards against double-send before React state catches up. */
  const sendLockRef = useRef(false);
  const prevSettingsOpen = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const stickToBottomRef = useRef(true);

  const hubProject = useMemo(
    () => (hubProjectId ? projects.find((p) => p.id === hubProjectId) ?? null : null),
    [hubProjectId, projects],
  );

  // Clear hub if project was deleted
  useEffect(() => {
    if (hubProjectId && !hubProject) setHubProjectId(null);
  }, [hubProjectId, hubProject]);

  // After login the login Dialog can unmount while still "open", leaving
  // Radix RemoveScroll's pointer-events:none on <body>. Clear it whenever
  // we enter the logged-in workspace.
  useEffect(() => {
    if (user) restoreBodyInteractivity();
  }, [user?.pubkey]);

  useSeoMeta({
    title: hubProject
      ? `${hubProject.name} · OpenDork`
      : activeChat
        ? activeProject
          ? `${activeChat.title || 'Chat'} · ${activeProject.name} · OpenDork`
          : `${activeChat.title || 'Chat'} · OpenDork`
        : 'OpenDork',
    description: 'Nostr-native AI workspace — BYOK, projects, encrypted history',
  });

  // Leave hub when user picks a chat from sidebar (activeChat changes while hub open)
  const openProjectHub = useCallback(
    (projectId: string) => {
      setHubProjectId(projectId);
      setHubInput('');
      setMobileSidebar(false);
      void selectProject(projectId);
    },
    [selectProject],
  );

  const closeProjectHub = useCallback(() => {
    setHubProjectId(null);
    setHubInput('');
    setHubAttachments([]);
    setHubNostrChips([]);
    setHubMentions([]);
    setHubPromptOmit([]);
  }, []);

  const sessionInput: SessionBlockInput = useMemo(() => {
    const profile = metadata
      ? {
          name: typeof metadata.name === 'string' ? metadata.name : undefined,
          displayName:
            typeof metadata.display_name === 'string' ? metadata.display_name : undefined,
          about: typeof metadata.about === 'string' ? metadata.about : undefined,
          nip05: typeof metadata.nip05 === 'string' ? metadata.nip05 : undefined,
          lud16: typeof metadata.lud16 === 'string' ? metadata.lud16 : undefined,
          website: typeof metadata.website === 'string' ? metadata.website : undefined,
          picture: typeof metadata.picture === 'string' ? metadata.picture : undefined,
        }
      : null;
    return {
      loggedIn: !!user,
      pubkey: user?.pubkey,
      profile,
      // Live configured pool (user NIP-65 after pull, or local edits)
      relayUrls: appConfig.relayMetadata.relays.map((r) => r.url),
      settings,
    };
  }, [user, metadata, appConfig.relayMetadata.relays, settings]);

  const openChatFromHub = useCallback(
    async (chatId: string) => {
      closeProjectHub();
      await selectChat(chatId);
    },
    [closeProjectHub, selectChat],
  );

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  useEffect(() => {
    const el = hubInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [hubInput]);

  // Smart auto-scroll: only stick to bottom if user hasn't scrolled up
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: isStreaming ? 'auto' : 'smooth' });
  }, [messages, isStreaming, streamBuffer?.content]);

  const onMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = dist < 80;
    stickToBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom && el.scrollHeight > el.clientHeight + 120);
  }, []);

  const scrollToBottom = useCallback(() => {
    stickToBottomRef.current = true;
    setShowScrollDown(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Clear attach error after a few seconds
  useEffect(() => {
    if (!attachError) return;
    const t = window.setTimeout(() => setAttachError(null), 4000);
    return () => clearTimeout(t);
  }, [attachError]);

  const displayMessages = streamBuffer
    ? messages.map((m) =>
        m.id === streamBuffer.id ? { ...m, content: streamBuffer.content } : m,
      )
    : messages;

  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);

    getAvailableModels()
      .then((modelsResponse) => {
        if (cancelled) return;
        const sorted = [...modelsResponse.data].sort((a, b) => {
          const costA = parseFloat(a.pricing.prompt) + parseFloat(a.pricing.completion);
          const costB = parseFloat(b.pricing.prompt) + parseFloat(b.pricing.completion);
          return costA - costB;
        });
        setModels(sorted);

        setSelectedModel((prev) => {
          const preferred =
            activeChat?.modelOverride ||
            activeProject?.defaultModelId ||
            settings.defaultModelId ||
            prev;
          if (preferred && sorted.some((m) => m.fullId === preferred)) {
            return preferred;
          }
          return sorted[0]?.fullId ?? '';
        });
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to fetch models:', err);
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection prefs read fresh on each run
  }, [user?.pubkey, getAvailableModels, modelsVersion, activeChat?.id, activeProject?.id]);

  // Refresh models when settings modal closes (providers may have changed)
  useEffect(() => {
    if (prevSettingsOpen.current && !showSettings) {
      setModelsVersion((v) => v + 1);
    }
    prevSettingsOpen.current = showSettings;
  }, [showSettings]);

  const openSettings = useCallback((tab: SettingsTab = 'instructions') => {
    setSettingsTab(tab);
    setShowSettings(true);
  }, []);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const queryNostr = useCallback(
    async (
      filters: Parameters<typeof nostr.query>[0],
      opts?: { signal?: AbortSignal },
    ) => nostr.query(filters, opts),
    [nostr],
  );

  const buildExtraBlocks = useCallback(
    async (
      project: typeof activeProject,
      history: Message[],
      userContent: string,
      signal?: AbortSignal,
      opts?: {
        chips?: NostrContextChip[];
        mentions?: ChatMention[];
      },
    ): Promise<string[]> => {
      const blocks: string[] = [];
      const consumedRefs: string[] = [];

      // 1) Explicit chips from this turn (or last user message on regenerate)
      const chips = opts?.chips ?? [];
      const turnMentions = opts?.mentions ?? [];

      // Also pull chips/mentions stored on the latest user message in history
      const lastUser = [...history].reverse().find((m) => m.role === 'user');
      const histChips = lastUser?.nostrContext ?? [];
      const histMentions = lastUser?.mentions ?? [];
      const allChips = chips.length ? chips : histChips;
      const allMentions = turnMentions.length ? turnMentions : histMentions;

      try {
        // Auto-attach @nostr network search when the message asks for it
        let workingChips = [...allChips];
        const hasNetwork = workingChips.some((c) => c.pack === 'network-search');
        if (!hasNetwork && messageWantsNetworkSearch(userContent)) {
          workingChips = [...workingChips, makeNetworkSearchChip()];
        }

        // Elevate any leftover mentions into chips so we fetch once
        // (skip sentinel network "mentions" if any leaked through)
        const peopleMentions = allMentions.filter(
          (m) => m.pubkey !== NOSTR_NETWORK_SENTINEL,
        );
        const { chips: unifiedChips, mentionsOnly } = mergeChipsAndMentions(
          workingChips,
          peopleMentions,
          { fetchNotes: settings.mentionAutoFetchNotes !== false },
        );

        if (unifiedChips.length) {
          const { block, consumedRefs: refs } = await buildPacksBlock(
            unifiedChips,
            queryNostr,
            {
              signal,
              selfPubkey: user?.pubkey,
              followPubkeys,
              userMessage: userContent,
            },
          );
          if (block) blocks.push(block);
          consumedRefs.push(...refs);
        }

        if (mentionsOnly.length) {
          const { block, consumedRefs: refs } = await buildMentionsBlock(
            mentionsOnly,
            queryNostr,
            {
              signal,
              fetchNotes: settings.mentionAutoFetchNotes !== false,
            },
          );
          if (block) blocks.push(block);
          consumedRefs.push(...refs);
        }

        // Auto-fetch scans the current message + recent history window
        // (older refs were already discussed; re-resolving them every turn
        // stacks relay latency for no benefit).
        const HISTORY_SCAN_WINDOW = 12;
        const sources: string[] = [userContent];
        for (const m of history.slice(-HISTORY_SCAN_WINDOW)) {
          if (m.content) sources.push(m.content);
        }

        // Project Nostr items:
        //  • network-search recipes → always live-expanded (follows search)
        //  • stale live packs (recent notes) → re-expand, stale cache fallback
        //  • fresh cached snapshots → already inlined by the context layer
        //  • static refs without cache → auto-fetch below
        if (project) {
          const projectChips: NostrContextChip[] = [];
          const staleCacheFallback = new Map<string, string>();

          for (const item of project.contextItems ?? []) {
            if (item.type !== 'nostr') {
              if (item.content) sources.push(item.content);
              continue;
            }
            const meta = item.meta;
            const pack = meta?.pack;
            const hasCache = !!meta?.cachedText?.trim();

            if (pack === 'network-search') {
              projectChips.push({
                id: item.id,
                title: item.title || 'Nostr · my follows',
                ref: meta?.ref || 'opendork:network-search',
                pack: 'network-search',
                snippet: meta?.snippet,
                noteLimit: meta?.noteLimit,
              });
              continue;
            }

            if (hasCache && !isContextCacheStale(meta)) {
              // Fresh snapshot is inlined by the context layer — just keep
              // the ref out of auto-fetch.
              if (item.content) consumedRefs.push(item.content);
              if (meta?.ref) consumedRefs.push(meta.ref);
              continue;
            }

            if (pack && pack !== 'single' && (!hasCache || isContextCacheStale(meta))) {
              const chip: NostrContextChip = {
                id: item.id,
                title: item.title || item.content,
                ref: meta?.ref || item.content,
                pack,
                pubkey: meta?.authorPubkey,
                noteLimit: meta?.noteLimit,
                snippet: meta?.snippet,
              };
              projectChips.push(chip);
              if (hasCache && isLiveContextPack(pack)) {
                staleCacheFallback.set(chip.id, meta!.cachedText!);
              }
              if (item.content) consumedRefs.push(item.content);
              if (meta?.ref) consumedRefs.push(meta.ref);
              continue;
            }

            if (item.content) sources.push(item.content);
          }

          if (projectChips.length) {
            const expanded = await Promise.all(
              projectChips.map(async (chip) => {
                const r = await expandChipSafely(chip, queryNostr, {
                  signal,
                  selfPubkey: user?.pubkey,
                  followPubkeys,
                  userMessage: userContent,
                });
                if (!r.ok) {
                  const fallback = staleCacheFallback.get(chip.id);
                  if (fallback) {
                    return {
                      text:
                        `### ${chip.title}\n` +
                        `_Cached snapshot (live refresh failed)._\n\n${fallback}`,
                      refs: chip.ref ? [chip.ref] : [],
                    };
                  }
                }
                return r;
              }),
            );
            const parts = expanded.map((e) => e.text);
            for (const e of expanded) consumedRefs.push(...e.refs);
            if (parts.length) {
              blocks.push(
                `# Project Nostr packs (live)\n` +
                  `Refreshed from relays for this send:\n\n` +
                  parts.join('\n\n---\n\n'),
              );
            }
          }
        }

        const auto = await buildNostrFetchBlock(sources, queryNostr, {
          signal,
          maxRefs: 8,
          skipRefs: consumedRefs,
        });
        if (auto) blocks.push(auto);
      } catch (err) {
        console.warn('Nostr fetch skipped:', err);
      }
      return blocks;
    },
    [queryNostr, user?.pubkey, settings.mentionAutoFetchNotes, followPubkeys],
  );

  /** Stream an assistant reply for an existing history (no new user message). */
  const streamAssistant = useCallback(
    async (opts: {
      chatId: string;
      project: typeof activeProject;
      chat:
        | NonNullable<typeof activeChat>
        | {
            id: string;
            instructionOverride?: string;
            promptOmit?: string[];
            personaId?: string;
            groundedOnly?: boolean;
          };
      historyForApi: Message[];
      /** Last user text for Nostr scan */
      lastUserText?: string;
      chips?: NostrContextChip[];
      mentions?: ChatMention[];
    }) => {
      const { chatId, project, chat, historyForApi, lastUserText, chips, mentions: turnMentions } =
        opts;
      if (!selectedModel || streamingIdRef.current) return;

      clearError();
      const assistantId = crypto.randomUUID();
      streamingIdRef.current = assistantId;
      await addMessage({
        id: assistantId,
        chatId,
        role: 'assistant',
        content: '',
        modelId: selectedModel,
      });

      setStreamBuffer({ id: assistantId, content: '' });
      setIsStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;
      let assembled = '';

      let turnReceipts: MessageReceipt[] = [];
      try {
        const extraBlocks = await buildExtraBlocks(
          project,
          historyForApi,
          lastUserText ?? '',
          controller.signal,
          { chips, mentions: turnMentions },
        );
        const promptOmit =
          'promptOmit' in chat && Array.isArray(chat.promptOmit)
            ? chat.promptOmit
            : undefined;
        const omitExtras = new Set(promptOmit ?? []);
        const filteredExtra =
          omitExtras.has('nostr_chips') && omitExtras.has('mentions')
            ? []
            : extraBlocks.filter((block) => {
                const head = block.slice(0, 80).toLowerCase();
                if (
                  omitExtras.has('mentions') &&
                  (head.includes('mention') || head.includes('@'))
                ) {
                  return false;
                }
                if (
                  omitExtras.has('nostr_chips') &&
                  (head.includes('pack') ||
                    head.includes('nostr context') ||
                    head.includes('fetched nostr') ||
                    head.includes('# fetched'))
                ) {
                  return false;
                }
                return true;
              });
        const built = buildApiMessagesDetailed(
          project,
          chat as typeof activeChat,
          historyForApi,
          {
            settings,
            extraBlocks: filteredExtra,
            session: omitExtras.has('session') ? null : sessionInput,
            omit: promptOmit,
          },
        );
        turnReceipts = built.receipts;
        await sendStreamingMessage(
          built.messages,
          selectedModel,
          (chunk) => {
            assembled += chunk;
            setStreamBuffer({ id: assistantId, content: assembled });
          },
          { signal: controller.signal },
        );
        void setDefaultModelId(selectedModel);
      } catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          console.error('Chat error:', err);
        }
      } finally {
        await updateMessage(assistantId, {
          content: assembled,
          ...(assembled.trim() && turnReceipts.length
            ? { receipts: turnReceipts }
            : {}),
        });
        abortRef.current = null;
        streamingIdRef.current = null;
        setStreamBuffer(null);
        setIsStreaming(false);
      }
    },
    [
      selectedModel,
      clearError,
      addMessage,
      updateMessage,
      sendStreamingMessage,
      setDefaultModelId,
      settings,
      buildExtraBlocks,
      sessionInput,
    ],
  );

  const runCompletion = useCallback(
    async (opts: {
      chatId: string;
      project: typeof activeProject;
      chat: NonNullable<typeof activeChat>;
      history: Message[];
      userContent: string;
      userAttachments?: MessageAttachment[];
      chips?: NostrContextChip[];
      mentions?: ChatMention[];
    }) => {
      const {
        chatId,
        project,
        chat,
        history,
        userContent,
        userAttachments,
        chips,
        mentions: turnMentions,
      } = opts;
      if (!selectedModel || isStreaming || sendLockRef.current) return;
      if (
        !userContent.trim() &&
        !(userAttachments?.length) &&
        !(chips?.length) &&
        !(turnMentions?.length)
      )
        return;

      // Soft intent: "my profile/history" with no chips → auto-attach packs
      let finalChips = [...(chips ?? [])];
      let finalMentions = [...(turnMentions ?? [])];
      // "my …" intent → auto-attach self packs. Notes/history intent maps to
      // my-recent (which already includes the profile block); a profile-only
      // ask gets just the profile — never a duplicate double-fetch.
      const intentNotes =
        /\b(my|mine)\b.{0,40}\b(history|notes|posts|nostr)\b/i.test(userContent) ||
        /\b(history|notes|posts)\b.{0,40}\b(my|mine)\b/i.test(userContent);
      const intentProfile =
        /\b(my|mine)\b.{0,40}\bprofile\b/i.test(userContent) ||
        /\bprofile\b.{0,40}\b(my|mine)\b/i.test(userContent);
      if (
        (intentNotes || intentProfile) &&
        user?.pubkey &&
        finalChips.length === 0 &&
        finalMentions.length === 0
      ) {
        const name =
          (typeof metadata?.display_name === 'string' && metadata.display_name) ||
          (typeof metadata?.name === 'string' && metadata.name) ||
          undefined;
        const picture =
          typeof metadata?.picture === 'string' ? metadata.picture : undefined;
        const nip05 =
          typeof metadata?.nip05 === 'string' ? metadata.nip05 : undefined;
        finalChips = [
          makeSelfChip(intentNotes ? 'my-recent' : 'my-profile', user.pubkey, name, {
            picture,
            nip05,
          }),
        ];
      }

      // Elevate remaining mentions into chips (dedupe with existing)
      const merged = mergeChipsAndMentions(finalChips, finalMentions, {
        fetchNotes: settings.mentionAutoFetchNotes !== false,
      });
      finalChips = merged.chips;
      // Mentions still stored on message for fidelity; fetch uses chips primarily
      // (buildExtraBlocks dedupes). Keep mentions array for UI/history.

      sendLockRef.current = true;
      clearError();

      try {
        const userMsg = await addMessage({
          chatId,
          role: 'user',
          content: userContent,
          attachments: userAttachments?.length ? userAttachments : undefined,
          mentions: finalMentions.length ? finalMentions : undefined,
          nostrContext: finalChips.length ? finalChips : undefined,
        });

        await streamAssistant({
          chatId,
          project,
          chat,
          historyForApi: [...history, userMsg],
          lastUserText: userContent,
          chips: finalChips,
          // Skip separate mentions block when already elevated to chips
          // (avoids double-fetch). Empty when all merged into chips.
          mentions: merged.mentionsOnly,
        });
      } finally {
        sendLockRef.current = false;
      }
    },
    [
      selectedModel,
      isStreaming,
      clearError,
      addMessage,
      streamAssistant,
      user?.pubkey,
      metadata,
      settings.mentionAutoFetchNotes,
    ],
  );

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (
      (!trimmed && attachments.length === 0 && nostrChips.length === 0 && mentions.length === 0) ||
      !selectedModel ||
      isStreaming ||
      !activeChat
    )
      return;
    const atts = attachments;
    const chips = nostrChips;
    const ments = syncMentionsWithText(trimmed, mentions);
    setInput('');
    setAttachments([]);
    setNostrChips([]);
    setMentions([]);
    setAttachError(null);
    await runCompletion({
      chatId: activeChat.id,
      project: activeProject,
      chat: activeChat,
      history: messages.filter((m) => m.chatId === activeChat.id),
      userContent: trimmed,
      userAttachments: atts,
      chips,
      mentions: ments,
    });
  }, [
    input,
    attachments,
    nostrChips,
    mentions,
    selectedModel,
    isStreaming,
    activeChat,
    activeProject,
    messages,
    runCompletion,
  ]);

  /** Start a new chat in the hub project and send the first message. */
  const handleHubSend = useCallback(async () => {
    const trimmed = hubInput.trim();
    if (
      (!trimmed &&
        hubAttachments.length === 0 &&
        hubNostrChips.length === 0 &&
        hubMentions.length === 0) ||
      !selectedModel ||
      isStreaming ||
      !hubProject
    )
      return;

    const atts = hubAttachments;
    const chips = hubNostrChips;
    const ments = syncMentionsWithText(trimmed, hubMentions);
    setHubInput('');
    setHubAttachments([]);
    setHubNostrChips([]);
    setHubMentions([]);
    const omit = [...hubPromptOmit];
    const chat = await createChat(hubProject.id);
    if (omit.length) {
      await updateChat(chat.id, { promptOmit: omit });
    }
    const chatForSend = omit.length ? { ...chat, promptOmit: omit } : chat;
    closeProjectHub();
    await runCompletion({
      chatId: chat.id,
      project: hubProject,
      chat: chatForSend,
      history: [],
      userContent: trimmed,
      userAttachments: atts,
      chips,
      mentions: ments,
    });
  }, [
    hubInput,
    hubAttachments,
    hubNostrChips,
    hubMentions,
    hubPromptOmit,
    selectedModel,
    isStreaming,
    hubProject,
    createChat,
    updateChat,
    closeProjectHub,
    runCompletion,
  ]);

  const handleNostrPickerAdd = useCallback(
    (chips: NostrContextChip[]) => {
      if (nostrPickerTarget === 'project' && hubProject) {
        // Enrich each chip (avatar, banner, about) before pinning to project
        void (async () => {
          const { chipToContextFields } = await import('@/lib/nostr/enrichContext');
          for (const c of chips) {
            try {
              const fields = await chipToContextFields(c, queryNostr, {
                selfPubkey: user?.pubkey,
              });
              await addContextItem(hubProject.id, fields);
            } catch {
              await addContextItem(hubProject.id, {
                type: 'nostr',
                title: c.title,
                content: c.ref,
                meta: {
                  ref: c.ref,
                  entity:
                    c.pack === 'thread'
                      ? 'thread'
                      : c.pack === 'article' || c.pack === 'single'
                        ? c.pack === 'article'
                          ? 'addr'
                          : 'event'
                        : 'profile',
                  authorPubkey: c.pubkey,
                  displayName: c.title,
                  nip05: c.nip05,
                  picture: c.picture,
                  image: c.image,
                  snippet: c.snippet,
                  pack: c.pack,
                  noteLimit: c.noteLimit,
                  cachedText: c.cachedText,
                },
              });
            }
          }
        })();
        return;
      }
      if (nostrPickerTarget === 'hub') {
        setHubNostrChips((prev) => {
          const ids = new Set(prev.map((p) => normalizeRefKey(p.ref) + '|' + p.pack));
          const next = [...prev];
          for (const c of chips) {
            if (!ids.has(normalizeRefKey(c.ref) + '|' + c.pack)) next.push(c);
          }
          return next;
        });
        return;
      }
      setNostrChips((prev) => {
        const ids = new Set(prev.map((p) => normalizeRefKey(p.ref) + '|' + p.pack));
        const next = [...prev];
        for (const c of chips) {
          if (!ids.has(normalizeRefKey(c.ref) + '|' + c.pack)) next.push(c);
        }
        return next;
      });
    },
    [nostrPickerTarget, hubProject, addContextItem, queryNostr, user?.pubkey],
  );

  const openNostrPicker = useCallback((target: 'main' | 'hub' | 'project') => {
    setNostrPickerTarget(target);
    setNostrPickerMode(target === 'project' ? 'project' : 'composer');
    setNostrPickerOpen(true);
  }, []);

  /** Resolve a pasted ref into a rich chip (avatar/title/cover when found). */
  const resolveChipForRef = useCallback(
    async (rawRef: string): Promise<NostrContextChip | null> => {
      const ref = rawRef.replace(/\s+/g, '');
      const parsed = parseNostrRef(ref);
      if (!parsed) return null;

      if (
        parsed.kind === 'npub' ||
        parsed.kind === 'nprofile' ||
        parsed.kind === 'hex-pubkey'
      ) {
        const pubkey = parsed.pubkey;
        let displayName: string | undefined;
        let nip05: string | undefined;
        let picture: string | undefined;
        try {
          const r = await fetchNostrEntity(npubOf(pubkey), queryNostr);
          if (r.ok) {
            displayName = r.meta?.displayName;
            nip05 = r.meta?.nip05;
            picture = r.meta?.picture;
          }
        } catch {
          /* keep bare chip */
        }
        return makeProfileChip(pubkey, {
          pack: 'profile-recent',
          displayName,
          nip05,
          picture,
        });
      }

      let pack: 'single' | 'thread' | 'article' =
        parsed.kind === 'naddr' && parsed.eventKind === 30023 ? 'article' : 'single';
      let title: string | undefined;
      let image: string | undefined;
      let picture: string | undefined;
      let pubkey: string | undefined;
      let eventId: string | undefined;
      let snippet: string | undefined;
      try {
        const r = await fetchNostrEntity(ref, queryNostr, {
          timeoutMs: parsed.kind === 'naddr' ? 14_000 : 10_000,
          includeAuthor: true,
        });
        if (r.ok) {
          title = r.meta?.title;
          image = r.meta?.image;
          picture = r.meta?.picture;
          pubkey = r.pubkey;
          eventId = r.eventId;
          if (r.meta?.eventKind === 30023) pack = 'article';
          snippet = r.text.split('\n').slice(0, 4).join(' ').slice(0, 160);
        }
      } catch {
        /* bare chip still works — resolves at send */
      }
      return makeEventChip(ref, { title, pack, snippet, image, picture, pubkey, eventId });
    },
    [queryNostr],
  );

  const handleAddRefChip = useCallback(
    (target: 'main' | 'hub') => async (ref: string) => {
      const chip = await resolveChipForRef(ref);
      if (!chip) return;
      const setChips = target === 'hub' ? setHubNostrChips : setNostrChips;
      setChips((prev) => {
        const key = normalizeRefKey(chip.ref) + '|' + chip.pack;
        if (prev.some((p) => normalizeRefKey(p.ref) + '|' + p.pack === key)) {
          return prev;
        }
        return [...prev, chip];
      });
    },
    [resolveChipForRef],
  );

  // Stable curried refs (composer memo deps)
  const addRefChipMain = useMemo(() => handleAddRefChip('main'), [handleAddRefChip]);
  const addRefChipHub = useMemo(() => handleAddRefChip('hub'), [handleAddRefChip]);

  /** Pin a pasted ref straight into a project's context pack. */
  const pinRefToProject = useCallback(
    async (ref: string, projectId: string) => {
      // Skip when the project already has this ref pinned
      const proj = projects.find((p) => p.id === projectId);
      const key = normalizeRefKey(ref);
      if (
        proj?.contextItems?.some(
          (i) => i.type === 'nostr' && normalizeRefKey(i.content) === key,
        )
      ) {
        return;
      }
      const chip = await resolveChipForRef(ref);
      try {
        const { chipToContextFields } = await import('@/lib/nostr/enrichContext');
        if (chip) {
          const fields = await chipToContextFields(chip, queryNostr, {
            selfPubkey: user?.pubkey,
          });
          await addContextItem(projectId, fields);
          return;
        }
        throw new Error('no chip');
      } catch {
        const p = chip?.pack;
        await addContextItem(projectId, {
          type: 'nostr',
          title: chip?.title || ref.slice(0, 24),
          content: chip?.ref || ref.replace(/\s+/g, ''),
          meta: {
            ref: chip?.ref || ref.replace(/\s+/g, ''),
            entity:
              p === 'thread'
                ? 'thread'
                : p === 'article'
                  ? 'addr'
                  : p === 'network-search'
                    ? 'pack'
                    : p === 'my-profile' ||
                        p === 'my-recent' ||
                        p === 'profile-brief' ||
                        p === 'profile-recent'
                      ? 'profile'
                      : 'event',
            pack: chip?.pack || 'single',
            authorPubkey: chip?.pubkey,
            displayName: chip?.title,
            picture: chip?.picture,
            image: chip?.image,
            snippet: chip?.snippet,
          },
        });
      }
    },
    [projects, resolveChipForRef, queryNostr, user?.pubkey, addContextItem],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const handleHubKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleHubSend();
      }
    },
    [handleHubSend],
  );

  const handleClear = useCallback(() => {
    if (!activeChat) return;
    if (messages.length > 0 && !confirm('Clear all messages in this chat?')) return;
    abortRef.current?.abort();
    abortRef.current = null;
    setStreamBuffer(null);
    setIsStreaming(false);
    void clearChatMessages(activeChat.id);
    clearError();
  }, [activeChat, clearChatMessages, clearError, messages.length]);

  const handleRetry = useCallback(async () => {
    if (!activeChat || isStreaming || !selectedModel) return;

    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === 'user') {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx < 0) return;

    const historyForApi = messages.slice(0, lastUserIdx + 1);
    await replaceMessages(activeChat.id, historyForApi);
    const lastUser = historyForApi[historyForApi.length - 1];
    await streamAssistant({
      chatId: activeChat.id,
      project: activeProject,
      chat: activeChat,
      historyForApi,
      lastUserText: lastUser?.content,
    });
  }, [
    activeChat,
    isStreaming,
    selectedModel,
    messages,
    replaceMessages,
    activeProject,
    streamAssistant,
  ]);

  /** Edit a user message: truncate thread from that point, replace text, regenerate. */
  const handleEditMessage = useCallback(
    async (messageId: string, newText: string) => {
      if (!activeChat || isStreaming || !selectedModel) return;
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0 || messages[idx]!.role !== 'user') return;

      const kept = messages.slice(0, idx);
      const original = messages[idx]!;
      const edited: Message = {
        ...original,
        content: newText,
        // keep attachments
      };
      const historyForApi = [...kept, edited];
      await replaceMessages(activeChat.id, historyForApi);
      await streamAssistant({
        chatId: activeChat.id,
        project: activeProject,
        chat: activeChat,
        historyForApi,
        lastUserText: newText,
      });
    },
    [
      activeChat,
      isStreaming,
      selectedModel,
      messages,
      replaceMessages,
      activeProject,
      streamAssistant,
    ],
  );

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (!activeChat || isStreaming) return;
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      // If deleting a user message, also drop the following assistant reply
      const toRemove = [messages[idx]!];
      if (
        messages[idx]!.role === 'user' &&
        messages[idx + 1]?.role === 'assistant'
      ) {
        toRemove.push(messages[idx + 1]!);
      }
      if (!confirm('Delete this message from the chat?')) return;
      const removeIds = new Set(toRemove.map((m) => m.id));
      const next = messages.filter((m) => !removeIds.has(m.id));
      await replaceMessages(activeChat.id, next);
    },
    [activeChat, isStreaming, messages, replaceMessages],
  );

  const handleFork = useCallback(
    async (throughMessageId: string) => {
      if (!activeChat || isStreaming) return;
      await forkChat(activeChat.id, throughMessageId);
    },
    [activeChat, isStreaming, forkChat],
  );

  const handleContinue = useCallback(async () => {
    if (!activeChat || isStreaming || !selectedModel) return;
    const historyForApi = [...messages];
    // Soft prompt to continue without adding a visible user bubble permanently —
    // we add a brief user nudge then stream
    const continueMsg = await addMessage({
      chatId: activeChat.id,
      role: 'user',
      content: 'Continue from where you left off.',
    });
    await streamAssistant({
      chatId: activeChat.id,
      project: activeProject,
      chat: activeChat,
      historyForApi: [...historyForApi, continueMsg],
      lastUserText: continueMsg.content,
    });
  }, [
    activeChat,
    isStreaming,
    selectedModel,
    messages,
    addMessage,
    activeProject,
    streamAssistant,
  ]);

  const handleRewrite = useCallback(
    async (preset: RewritePreset) => {
      if (!activeChat || isStreaming || !selectedModel) return;
      const prompts: Record<RewritePreset, string> = {
        shorter: 'Rewrite your previous reply to be shorter and tighter. Keep the meaning.',
        precise:
          'Rewrite your previous reply to be more precise and specific. Remove fluff.',
        bullets:
          'Rewrite your previous reply as a clear bullet list. Keep key points only.',
      };
      const text = prompts[preset];
      const userMsg = await addMessage({
        chatId: activeChat.id,
        role: 'user',
        content: text,
      });
      await streamAssistant({
        chatId: activeChat.id,
        project: activeProject,
        chat: activeChat,
        historyForApi: [...messages, userMsg],
        lastUserText: text,
      });
    },
    [
      activeChat,
      isStreaming,
      selectedModel,
      messages,
      addMessage,
      activeProject,
      streamAssistant,
    ],
  );

  const handleSearchSelect = useCallback(
    async (hit: WorkspaceSearchHit) => {
      if (hit.chatId) {
        closeProjectHub();
        await selectChat(hit.chatId);
        if (hit.messageId) setScrollToMessageId(hit.messageId);
        return;
      }
      // Project-only hit (memory / context / instructions)
      if (hit.projectId) {
        openProjectHub(hit.projectId);
      }
    },
    [closeProjectHub, selectChat, openProjectHub],
  );

  const handlePinMemory = useCallback(
    async (msg: Message) => {
      const project = activeProject;
      if (!project) return;
      const text = clipForPin(msg.content || '');
      if (!text) return;
      const title =
        msg.role === 'assistant'
          ? 'From assistant'
          : 'From chat';
      await addMemoryItem(project.id, {
        content: text,
        title,
        source: 'chat',
      });
    },
    [activeProject, addMemoryItem],
  );

  const handlePinContext = useCallback(
    async (msg: Message) => {
      const project = activeProject;
      if (!project) return;
      const text = clipForPin(msg.content || '', 4000);
      if (!text) return;
      await addContextItem(project.id, {
        type: 'note',
        title:
          msg.role === 'assistant'
            ? `Pinned reply · ${new Date(msg.createdAt).toLocaleDateString()}`
            : `Pinned message · ${new Date(msg.createdAt).toLocaleDateString()}`,
        content: text,
        pinned: true,
      });
    },
    [activeProject, addContextItem],
  );

  /** Promote Nostr chips attached to a message into the project context pack. */
  const handlePinMessageNostr = useCallback(
    async (msg: Message) => {
      const project = activeProject;
      const chips = msg.nostrContext ?? [];
      if (!project || !chips.length) return;
      const existing = new Set(
        (project.contextItems ?? [])
          .filter((i) => i.type === 'nostr')
          .map((i) => normalizeRefKey(i.content)),
      );
      const { chipToContextFields } = await import('@/lib/nostr/enrichContext');
      for (const chip of chips) {
        const key = normalizeRefKey(chip.ref);
        if (existing.has(key)) continue;
        existing.add(key);
        try {
          const fields = await chipToContextFields(chip, queryNostr, {
            selfPubkey: user?.pubkey,
          });
          await addContextItem(project.id, fields);
        } catch {
          await addContextItem(project.id, {
            type: 'nostr',
            title: chip.title,
            content: chip.ref,
            meta: {
              ref: chip.ref,
              entity:
                chip.pack === 'thread'
                  ? 'thread'
                  : chip.pack === 'article'
                    ? 'addr'
                    : chip.pack === 'network-search'
                      ? 'pack'
                      : chip.pack === 'single'
                        ? 'event'
                        : 'profile',
              pack: chip.pack,
              authorPubkey: chip.pubkey,
              displayName: chip.title,
              nip05: chip.nip05,
              picture: chip.picture,
              image: chip.image,
              snippet: chip.snippet,
              noteLimit: chip.noteLimit,
            },
          });
        }
      }
    },
    [activeProject, addContextItem, queryNostr, user?.pubkey],
  );

  const effectivePersonaId = resolvePersonaId(activeChat, settings);
  const effectiveGrounded = resolveGroundedOnly(activeChat, settings);

  const handlePersonaChange = useCallback(
    (id: PersonaId) => {
      if (activeChat) {
        void updateChat(activeChat.id, { personaId: id });
      } else {
        void updateSettings({ defaultPersonaId: id });
      }
    },
    [activeChat, updateChat, updateSettings],
  );

  const handleGroundedChange = useCallback(
    (on: boolean) => {
      if (activeChat) {
        void updateChat(activeChat.id, { groundedOnly: on });
      } else {
        void updateSettings({ groundedOnly: on });
      }
    },
    [activeChat, updateChat, updateSettings],
  );

  // Scroll to a message after search navigation
  useEffect(() => {
    if (!scrollToMessageId || isStreaming) return;
    const el = document.querySelector(
      `[data-message-id="${scrollToMessageId}"]`,
    );
    if (el) {
      stickToBottomRef.current = false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToMessageId(null);
    }
  }, [scrollToMessageId, messages, isStreaming]);

  // New chat → stick to bottom again
  useEffect(() => {
    stickToBottomRef.current = true;
    setShowScrollDown(false);
  }, [activeChat?.id]);

  const selectedModelMeta = useMemo(
    () => models.find((m) => m.fullId === selectedModel) ?? null,
    [models, selectedModel],
  );

  const tokenEstimate = useMemo(() => {
    const used = estimateOutgoingTokens({
      project: hubProject ?? activeProject,
      chat: hubProject ? null : activeChat,
      settings,
      history: hubProject ? [] : messages,
      draft: hubProject ? hubInput : input,
      draftAttachments: hubProject ? hubAttachments : attachments,
      session: sessionInput,
      omit: hubProject ? hubPromptOmit : activeChat?.promptOmit,
    });
    const window = resolveContextWindow(selectedModelMeta);
    return { used, window };
  }, [
    hubProject,
    activeProject,
    activeChat,
    settings,
    messages,
    hubInput,
    input,
    hubAttachments,
    attachments,
    selectedModelMeta,
    sessionInput,
    hubPromptOmit,
  ]);

  const usingShakespeare = selectedModel.startsWith('shakespeare/');
  const isEmpty = messages.length === 0;
  const greetName = greetingNameFromMetadata(metadata);
  // Avoid flashing the generic line then swapping once kind-0 arrives.
  // While logged in and profile is still resolving, hold a stable blank line
  // of the same size; once settled, show personalized or generic.
  const waitingOnProfile = !!user && !greetName && profileLoading;
  const emptyGreeting = greetName
    ? `Hey ${greetName}, how can I help you today?`
    : waitingOnProfile
      ? '\u00a0'
      : 'How can I help you today?';

  const contextLayers = useMemo(() => {
    const project = hubProject ?? activeProject;
    const chips = hubProject ? hubNostrChips : nostrChips;
    const ments = hubProject ? hubMentions : mentions;
    const atts = hubProject ? hubAttachments : attachments;
    return buildContextPreview({
      project,
      chat: hubProject ? null : activeChat,
      settings,
      sessionLoggedIn: !!user,
      chipCount: chips.length,
      mentionCount: ments.length,
      attachmentCount: atts.length,
      chipLabels: chips.map((c) => c.title?.trim() || c.ref || 'pack'),
      mentionLabels: ments.map((m) => m.displayName || m.npub || 'mention'),
      attachmentLabels: atts.map(
        (a) => a.name?.trim() || (a.kind === 'image' ? 'Image' : 'File'),
      ),
      omit: hubProject ? hubPromptOmit : undefined,
    });
  }, [
    hubProject,
    activeProject,
    activeChat,
    settings,
    user,
    hubNostrChips,
    nostrChips,
    hubMentions,
    mentions,
    hubAttachments,
    attachments,
    hubPromptOmit,
  ]);

  const contextHint = useMemo(() => {
    return contextLayers
      .filter((l) => l.enabled)
      .map((l) => {
        if (l.items?.length) {
          const n = l.items.filter((i) => i.enabled).length;
          return n > 0 ? `${l.label} (${n})` : null;
        }
        return l.count != null && l.count > 0
          ? `${l.label} (${l.count})`
          : l.label;
      })
      .filter(Boolean)
      .join(' · ');
  }, [contextLayers]);

  const applyOmitToggle = useCallback((prev: string[], key: string, enabled: boolean): string[] => {
    let next: string[];
    if (enabled) {
      next = prev.filter((k) => k !== key);
      if (key.startsWith('memory:') && prev.includes('memory')) {
        next = next.filter((k) => k !== 'memory');
      }
      if (key.startsWith('context:') && prev.includes('context')) {
        next = next.filter((k) => k !== 'context');
      }
      if (key === 'memory') {
        next = next.filter((k) => !k.startsWith('memory:'));
      }
      if (key === 'context') {
        next = next.filter((k) => !k.startsWith('context:'));
      }
    } else {
      next = prev.includes(key) ? [...prev] : [...prev, key];
      if (key === 'memory') {
        next = next.filter((k) => !k.startsWith('memory:'));
        if (!next.includes('memory')) next.push('memory');
      }
      if (key === 'context') {
        next = next.filter((k) => !k.startsWith('context:'));
        if (!next.includes('context')) next.push('context');
      }
    }
    return next;
  }, []);

  const handleContextToggle = useCallback(
    (key: string, enabled: boolean) => {
      // Project hub (no chat yet): keep omits in local state until first send
      if (hubProject) {
        if (!enabled) {
          if (key === 'nostr_chips') setHubNostrChips([]);
          if (key === 'mentions') setHubMentions([]);
          if (key === 'attachments') setHubAttachments([]);
        }
        setHubPromptOmit((prev) => applyOmitToggle(prev, key, enabled));
        return;
      }

      if (!activeChat) {
        if (!enabled) {
          if (key === 'nostr_chips') setNostrChips([]);
          if (key === 'mentions') setMentions([]);
          if (key === 'attachments') setAttachments([]);
        }
        return;
      }

      const prev = activeChat.promptOmit ?? [];
      const next = applyOmitToggle(prev, key, enabled);

      if (!enabled) {
        if (key === 'nostr_chips') setNostrChips([]);
        if (key === 'mentions') setMentions([]);
        if (key === 'attachments') setAttachments([]);
      }

      void updateChat(activeChat.id, { promptOmit: next });
    },
    [activeChat, hubProject, updateChat, applyOmitToggle],
  );

  const stylePicker = (
    <StylePicker
      personaId={
        hubProject
          ? settings.defaultPersonaId || 'default'
          : effectivePersonaId
      }
      onPersonaChange={(id) => {
        if (hubProject) {
          void updateSettings({ defaultPersonaId: id });
        } else {
          handlePersonaChange(id);
        }
      }}
      groundedOnly={
        hubProject ? !!settings.groundedOnly : effectiveGrounded
      }
      onGroundedChange={(on) => {
        if (hubProject) {
          void updateSettings({ groundedOnly: on });
        } else {
          handleGroundedChange(on);
        }
      }}
      scope={hubProject || !activeChat ? 'default' : 'chat'}
      disabled={isStreaming}
    />
  );

  const modelPicker = (
    <ModelPicker
      models={models}
      modelsLoading={modelsLoading}
      selectedModel={selectedModel}
      onSelect={(id) => {
        setSelectedModel(id);
        void setDefaultModelId(id);
      }}
      creditsBalance={creditsBalance}
      usingShakespeare={usingShakespeare}
      onOpenProviders={() => openSettings('providers')}
      placement="up"
    />
  );

  const shortcutHandlers = useMemo(
    () => ({
      // Freestanding chat (Recents) — same as sidebar "New chat"
      onNewChat: () => {
        closeProjectHub();
        void createChat(null);
      },
      onFocusInput: () => {
        if (hubProjectId) hubInputRef.current?.focus();
        else inputRef.current?.focus();
      },
      onSearch: () => setShowSearch(true),
      onToggleSidebar: () => {
        if (window.matchMedia('(min-width: 768px)').matches) {
          setSidebarCollapsed((c) => !c);
        } else {
          setMobileSidebar((m) => !m);
        }
      },
      onOpenProviders: () => openSettings('providers'),
      onOpenWorkspace: () => openSettings('privacy'),
      onOpenProject: () => {
        if (activeProject) openProjectHub(activeProject.id);
        else openSettings('instructions');
      },
      onStop: () => {
        abortRef.current?.abort();
      },
    }),
    [createChat, openSettings, closeProjectHub, hubProjectId, activeProject, openProjectHub],
  );

  const lastAssistantId = useMemo(() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      if (displayMessages[i]!.role === 'assistant') return displayMessages[i]!.id;
    }
    return null;
  }, [displayMessages]);

  const messageActionsFor = useCallback(
    (msg: Message): MessageActions | undefined => {
      if (isStreaming) return undefined;
      const canPin = !!activeProject && !!(msg.content?.trim());
      return {
        onEditSubmit:
          msg.role === 'user'
            ? (text) => void handleEditMessage(msg.id, text)
            : undefined,
        onRegenerate:
          msg.role === 'assistant' && msg.id === lastAssistantId
            ? () => void handleRetry()
            : undefined,
        onContinue:
          msg.role === 'assistant' && msg.id === lastAssistantId
            ? () => void handleContinue()
            : undefined,
        onRewrite:
          msg.role === 'assistant' ? (p) => void handleRewrite(p) : undefined,
        onFork: () => void handleFork(msg.id),
        onDelete: () => void handleDeleteMessage(msg.id),
        onPinMemory: canPin ? () => void handlePinMemory(msg) : undefined,
        onPinContext: canPin ? () => void handlePinContext(msg) : undefined,
        onPinNostr:
          activeProject && (msg.nostrContext?.length ?? 0) > 0
            ? () => void handlePinMessageNostr(msg)
            : undefined,
      };
    },
    [
      isStreaming,
      lastAssistantId,
      activeProject,
      handleEditMessage,
      handleRetry,
      handleContinue,
      handleRewrite,
      handleFork,
      handleDeleteMessage,
      handlePinMemory,
      handlePinContext,
      handlePinMessageNostr,
    ],
  );

  const composerShared = {
    selectedModel,
    isStreaming,
    modelPicker,
    contextHint,
    contextLayers,
    onContextToggle: handleContextToggle,
    // Thread instructions need an existing chat — not on the hub / pre-first-send
    threadInstructions:
      !hubProject && activeChat ? activeChat.instructionOverride : undefined,
    onSaveThreadInstructions:
      !hubProject && activeChat
        ? (text: string) =>
            void updateChat(activeChat.id, {
              instructionOverride: text || undefined,
            })
        : undefined,
    stylePicker,
    usingShakespeare,
    tokenUsed: tokenEstimate.used,
    tokenWindow: tokenEstimate.window,
    onAttachError: (msg: string) => setAttachError(msg),
    mentionCandidates,
    mentionsLoading: followsLoading,
  };

  useKeyboardShortcuts(shortcutHandlers, !!user && ready);

  // ─── Not logged in ───

  if (!user) {
    return (
      <div className="min-h-dvh bg-[#0a0a0a] text-[#e8e6e3] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-5">
            <OpenDorkWordmark className="w-48 sm:w-56 text-[#B7B1B1]" />
            <p className="text-center text-[15px] text-[#6a6864] max-w-sm leading-relaxed">
              Nostr-native AI workspace. Bring your own keys, organize projects, encrypt history.
            </p>
          </div>
          <LoginArea className="w-full flex" />
          <div className="w-full grid gap-2">
            {[
              'Log in with extension, nsec, bunker, or remote signer',
              'Add OpenRouter / Ollama — or use Shakespeare credits',
              'Projects, memory, vault encryption & optional relay sync',
            ].map((line) => (
              <div
                key={line}
                className="flex items-start gap-3 rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] px-3.5 py-2.5 text-[13px] text-[#5a5a5a] leading-relaxed"
              >
                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#3a3a3a] shrink-0" />
                {line}
              </div>
            ))}
          </div>
          <p className="text-center text-[#2a2a2a] text-[11px]">
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              className="text-[#3a3a3a] hover:text-[#5a5a5a] transition-colors"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-dvh bg-[#0a0a0a] text-[#5a5a5a] flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 rounded-full border border-[#2a2a2a] border-t-[#8a8884] animate-spin" />
        <span className="text-[13px]">Loading workspace…</span>
      </div>
    );
  }

  // ─── Logged in workspace ───

  return (
    <AccountIsolationGate>
    <VaultUnlockGate>
      <div className="h-dvh bg-[#0a0a0a] text-[#e8e6e3] flex overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden md:flex min-h-0">
          <WorkspaceSidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((c) => !c)}
            onOpenSettings={openSettings}
            onOpenProjectSettings={() => {
              if (activeProject) openProjectHub(activeProject.id);
              else openSettings('instructions');
            }}
            onOpenProjectHub={openProjectHub}
            projectHubOpen={!!hubProject}
            onLeaveProjectHub={closeProjectHub}
            onOpenSearch={() => setShowSearch(true)}
          />
        </div>

        {/* Mobile sidebar drawer */}
        {mobileSidebar && (
          <div className="md:hidden fixed inset-0 z-40 flex">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
              onClick={() => setMobileSidebar(false)}
            />
            <div className="relative z-10 h-full flex shadow-2xl od-enter">
              <WorkspaceSidebar
                collapsed={false}
                onToggle={() => setMobileSidebar(false)}
                onOpenSettings={(tab) => {
                  openSettings(tab);
                  setMobileSidebar(false);
                }}
                onOpenProjectSettings={() => {
                  if (activeProject) openProjectHub(activeProject.id);
                  else openSettings('instructions');
                  setMobileSidebar(false);
                }}
                onOpenProjectHub={(id) => {
                  openProjectHub(id);
                  setMobileSidebar(false);
                }}
                projectHubOpen={!!hubProject}
                onLeaveProjectHub={closeProjectHub}
                onOpenSearch={() => {
                  setShowSearch(true);
                  setMobileSidebar(false);
                }}
              />
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Minimal top bar — status lives in the sidebar */}
          <header
            className={cn(
              'shrink-0 px-3 sm:px-5 h-12 flex items-center justify-between gap-2',
              !hubProject && !isEmpty && 'border-b border-[#141414]/80',
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setMobileSidebar(true)}
                className="md:hidden p-2 -ml-1 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] shrink-0"
                aria-label="Open sidebar"
              >
                <Menu className="w-4 h-4" />
              </button>
              {!hubProject && !isEmpty && (
                <div className="flex items-center gap-1.5 min-w-0">
                  {activeProject && (
                    <>
                      <button
                        type="button"
                        onClick={() => openProjectHub(activeProject.id)}
                        className="text-[12px] text-[#5a5a5a] hover:text-[#e8e6e3] truncate max-w-[120px] sm:max-w-[180px] transition-colors"
                      >
                        {activeProject.name}
                      </button>
                      <span className="text-[#2a2a2a] text-[12px] select-none">/</span>
                    </>
                  )}
                  <h1 className="text-[13px] font-medium text-[#e8e6e3] truncate max-w-[min(100%,320px)] tracking-tight">
                    {activeChat?.title || 'New chat'}
                  </h1>
                </div>
              )}
              {hubProject && (
                <div className="flex items-center gap-1.5 min-w-0 text-[12px] text-[#5a5a5a]">
                  <span className="hidden sm:inline">Projects</span>
                  <span className="hidden sm:inline text-[#2a2a2a]">/</span>
                  <span className="text-[#e8e6e3] font-medium truncate tracking-tight">
                    {hubProject.name}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                className="p-2 rounded-lg text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
                title="Search (⌘K)"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
              {!hubProject && !isEmpty && (
                <>
                  <button
                    type="button"
                    onClick={() => void handleRetry()}
                    disabled={isStreaming || messages.every((m) => m.role !== 'user')}
                    className="p-2 rounded-lg text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors disabled:opacity-30"
                    title="Regenerate last reply"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={messages.length === 0}
                    className="p-2 rounded-lg text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors disabled:opacity-30"
                    title="Clear chat"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </header>

          <SettingsModal
            open={showSettings}
            onClose={() => setShowSettings(false)}
            tab={settingsTab}
            onTabChange={setSettingsTab}
          />

          <ChatSearchDialog
            open={showSearch}
            onClose={() => setShowSearch(false)}
            onSelect={(hit) => void handleSearchSelect(hit)}
          />

          {/* Main */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {hubProject ? (
              <ProjectHubView
                project={hubProject}
                onOpenChat={(id) => void openChatFromHub(id)}
                onRenameProject={() => {
                  const name = window.prompt('Project name', hubProject.name);
                  if (name?.trim()) void updateProject(hubProject.id, { name: name.trim() });
                }}
                onExportProject={() => void exportProject(hubProject.id)}
                onDeleteProject={() => {
                  if (
                    confirm(
                      `Delete project “${hubProject.name}” and all its chats?`,
                    )
                  ) {
                    void deleteProject(hubProject.id).then(() => closeProjectHub());
                  }
                }}
                composer={
                  <ChatInput
                    centered
                    inputRef={hubInputRef}
                    input={hubInput}
                    setInput={setHubInput}
                    handleKeyDown={handleHubKeyDown}
                    handleSend={() => void handleHubSend()}
                    handleStop={handleStop}
                    attachments={hubAttachments}
                    onAttachmentsChange={setHubAttachments}
                    nostrChips={hubNostrChips}
                    onNostrChipsChange={setHubNostrChips}
                    mentions={hubMentions}
                    onMentionsChange={setHubMentions}
                    onOpenNostrPicker={() => openNostrPicker('hub')}
                    onAddRefChip={addRefChipHub}
                    onPinRefToProject={
                      hubProject ? (ref) => pinRefToProject(ref, hubProject.id) : undefined
                    }
                    {...composerShared}
                  />
                }
                onOpenNostrPicker={() => openNostrPicker('project')}
              />
            ) : isEmpty ? (
              <div className="flex-1 flex flex-col items-center justify-center px-4 pb-10">
                <div className="mb-8 sm:mb-10 text-center max-w-lg">
                  <h2
                    className={cn(
                      'text-[1.65rem] sm:text-[1.85rem] font-medium text-[#e8e6e3] tracking-tight transition-opacity duration-200',
                      waitingOnProfile && 'opacity-0',
                    )}
                    style={{ letterSpacing: '-0.03em' }}
                    aria-busy={waitingOnProfile || undefined}
                  >
                    {emptyGreeting}
                  </h2>
                  {activeProject && (
                    <p className="mt-2 text-[13px] text-[#5a5a5a]">
                      In{' '}
                      <button
                        type="button"
                        onClick={() => openProjectHub(activeProject.id)}
                        className="text-[#8a8884] hover:text-[#e8e6e3] transition-colors"
                      >
                        {activeProject.name}
                      </button>
                    </p>
                  )}
                </div>
                {attachError && (
                  <p className="mb-3 text-[12px] text-amber-500/90 text-center max-w-md">
                    {attachError}
                  </p>
                )}
                <ChatInput
                  centered
                  inputRef={inputRef}
                  input={input}
                  setInput={setInput}
                  handleKeyDown={handleKeyDown}
                  handleSend={() => void handleSend()}
                  handleStop={handleStop}
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  nostrChips={nostrChips}
                  onNostrChipsChange={setNostrChips}
                  mentions={mentions}
                  onMentionsChange={setMentions}
                  onOpenNostrPicker={() => openNostrPicker('main')}
                  onAddRefChip={addRefChipMain}
                  onPinRefToProject={
                    activeProject ? (ref) => pinRefToProject(ref, activeProject.id) : undefined
                  }
                  {...composerShared}
                />
              </div>
            ) : (
              <>
                <div className="relative flex-1 min-h-0 flex flex-col">
                  <div
                    ref={messagesScrollRef}
                    onScroll={onMessagesScroll}
                    className="flex-1 overflow-y-auto scrollbar-thin"
                  >
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-7 sm:space-y-8">
                      {displayMessages.map((msg) => (
                        <MessageLine
                          key={msg.id}
                          message={msg}
                          isStreaming={
                            isStreaming &&
                            msg.role === 'assistant' &&
                            msg.id === displayMessages[displayMessages.length - 1]?.id
                          }
                          isLastAssistant={msg.id === lastAssistantId}
                          actions={messageActionsFor(msg)}
                        />
                      ))}

                      {isStreaming &&
                        displayMessages[displayMessages.length - 1]?.role === 'assistant' &&
                        !displayMessages[displayMessages.length - 1]?.content && (
                          <div className="flex items-center gap-3 -mt-2 pl-0.5">
                            <DorkThinking className="text-sm text-[#3a3a3a]" />
                            <span className="text-[12px] text-[#3a3a3a]">Thinking…</span>
                          </div>
                        )}

                      {apiError && (
                        <div className="rounded-xl border border-red-500/20 bg-red-950/20 px-4 py-3 text-sm text-red-400 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {retryAfter ? (
                              <span>Rate limited. Slow down or add credits at shakespeare.diy</span>
                            ) : apiError.includes('run out of credits') ? (
                              <span>
                                Out of credits. Add more at{' '}
                                <a
                                  href="https://shakespeare.diy"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-red-300 hover:underline"
                                >
                                  shakespeare.diy
                                </a>
                              </span>
                            ) : (
                              <span className="break-words">{apiError}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => clearError()}
                            className="shrink-0 p-1 rounded-md text-red-400/60 hover:text-red-300"
                            title="Dismiss"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      <div ref={messagesEndRef} className="h-px" />
                    </div>
                  </div>

                  {showScrollDown && (
                    <button
                      type="button"
                      onClick={scrollToBottom}
                      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#2a2a2a] bg-[#141414]/95 backdrop-blur-sm text-[12px] text-[#8a8884] hover:text-[#e8e6e3] shadow-lg od-enter"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                      Jump to latest
                    </button>
                  )}
                </div>

                <div className="shrink-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/95 to-transparent px-4 sm:px-6 pt-3 pb-4 sm:pb-5">
                  {attachError && (
                    <p className="mb-2 text-center text-[11px] text-amber-500/90">
                      {attachError}
                    </p>
                  )}
                  <ChatInput
                    inputRef={inputRef}
                    input={input}
                    setInput={setInput}
                    handleKeyDown={handleKeyDown}
                    handleSend={() => void handleSend()}
                    handleStop={handleStop}
                    attachments={attachments}
                    onAttachmentsChange={setAttachments}
                    nostrChips={nostrChips}
                    onNostrChipsChange={setNostrChips}
                    mentions={mentions}
                    onMentionsChange={setMentions}
                    onOpenNostrPicker={() => openNostrPicker('main')}
                    onAddRefChip={addRefChipMain}
                    onPinRefToProject={
                      activeProject ? (ref) => pinRefToProject(ref, activeProject.id) : undefined
                    }
                    {...composerShared}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <NostrPicker
        open={nostrPickerOpen}
        onClose={() => setNostrPickerOpen(false)}
        mode={nostrPickerMode}
        onAddChips={handleNostrPickerAdd}
      />
    </VaultUnlockGate>
    </AccountIsolationGate>
  );
}

export default AIChatPage;
